import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Role } from "@hrms/types";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { Input } from "../../components/ui/input.js";
import { Button } from "../../components/ui/button.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Checkbox } from "../../components/ui/checkbox.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs.js";
import CompStructurePanel, { CollapseToggle } from "./CompStructurePanel.js";

// ── Types (mirror GET /compensation/statutory-config) ──

type RegimeKey = "NEW_REGIME" | "OLD_REGIME";
interface TaxRegimeRules {
  standardDeduction: number; rebateIncomeLimit: number; rebateMax: number; rebateMarginalRelief: boolean;
  section80CCap: number; deductProfTax: boolean;
  slabs: { upTo: number | null; rate: number }[];
  surcharge: { above: number; rate: number }[];
}
// pfWageCeilingMonthly / esiWageThresholdMonthly are still stored and sent back unchanged, but the
// engine no longer reads them: PF/ESI/Gratuity rules live in the versioned salary structure.
interface StatutoryConfig {
  fiscalYear: string; pfWageCeilingMonthly: number; esiWageThresholdMonthly: number; cessRate: number;
  taxRegimes: Record<RegimeKey, TaxRegimeRules>;
}

// ── Display helpers ──────────────────────────────────────────────────────────

const NUM = "h-8 text-right tabular-nums";
const toPct = (frac: number) => String(+(frac * 100).toFixed(4));
const fromPct = (pct: string) => +(parseFloat(pct) / 100).toFixed(6);
const rupees = (n: number) => `₹${n.toLocaleString("en-IN")}`;

// ── Statutory & income-tax settings ──────────────────────────────────────────

function NumberField({ id, label, value, onChange, disabled, suffix, hint }: {
  id: string; label: string; value: string; onChange: (v: string) => void; disabled: boolean; suffix?: string; hint?: string;
}) {
  return (
    <label htmlFor={id} className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-surface-500">{label}</span>
      <span className="flex items-center gap-1">
        <Input id={id} type="number" min={0} step="any" className={NUM} disabled={disabled} value={value} onChange={e => onChange(e.target.value)} />
        {suffix && <span className="text-[12px] text-surface-500">{suffix}</span>}
      </span>
      {hint && <span className="text-[11px] text-surface-400">{hint}</span>}
    </label>
  );
}

function RegimeEditor({ regime, rules, onChange, disabled }: {
  regime: RegimeKey; rules: TaxRegimeRules; onChange: (r: TaxRegimeRules) => void; disabled: boolean;
}) {
  const set = (patch: Partial<TaxRegimeRules>) => onChange({ ...rules, ...patch });
  const num = (s: string) => (s === "" ? 0 : Number(s));
  const setSlab = (i: number, patch: Partial<TaxRegimeRules["slabs"][number]>) =>
    set({ slabs: rules.slabs.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const setSur = (i: number, patch: Partial<TaxRegimeRules["surcharge"][number]>) =>
    set({ surcharge: rules.surcharge.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  // A new slab is inserted before the open-ended top slab.
  const addSlab = () => {
    const open = rules.slabs[rules.slabs.length - 1]!;
    const prevTop = rules.slabs.length > 1 ? rules.slabs[rules.slabs.length - 2]!.upTo ?? 0 : 0;
    set({ slabs: [...rules.slabs.slice(0, -1), { upTo: prevTop + 400000, rate: open.rate }, open] });
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
        <NumberField id={`${regime}-std`} label="Standard deduction" suffix="₹" disabled={disabled}
          value={String(rules.standardDeduction)} onChange={v => set({ standardDeduction: num(v) })} />
        <NumberField id={`${regime}-rebate-limit`} label="87A rebate: taxable up to" suffix="₹" disabled={disabled}
          value={String(rules.rebateIncomeLimit)} onChange={v => set({ rebateIncomeLimit: num(v) })} />
        <NumberField id={`${regime}-rebate-max`} label="87A rebate: maximum" suffix="₹" disabled={disabled}
          value={String(rules.rebateMax)} onChange={v => set({ rebateMax: num(v) })} />
        <NumberField id={`${regime}-80c`} label="80C cap (employee PF)" suffix="₹" disabled={disabled}
          hint="0 = no 80C deduction" value={String(rules.section80CCap)} onChange={v => set({ section80CCap: num(v) })} />
      </div>
      <div className="flex flex-wrap gap-6 text-[13px] text-surface-700">
        <label className="flex items-center gap-2">
          <Checkbox disabled={disabled} checked={rules.rebateMarginalRelief}
            onCheckedChange={v => set({ rebateMarginalRelief: v === true })} />
          Marginal relief just above the rebate limit
        </label>
        <label className="flex items-center gap-2">
          <Checkbox disabled={disabled} checked={rules.deductProfTax}
            onCheckedChange={v => set({ deductProfTax: v === true })} />
          Deduct professional tax from taxable income
        </label>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="min-w-0">
          <div className="mb-2 text-[12px] font-semibold text-surface-700">Tax slabs (on taxable income)</div>
          <Table>
            <TableHeader><TableRow><TableHead>Up to ₹</TableHead><TableHead className="text-right">Rate</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rules.slabs.map((s, i) => {
                const last = i === rules.slabs.length - 1;
                return (
                  <TableRow key={i}>
                    <TableCell>
                      {last ? <span className="text-[12px] text-surface-500">and above</span> : (
                        <Input type="number" min={0} step="10000" className={NUM} disabled={disabled} aria-label={`Slab ${i + 1} upper limit`}
                          value={String(s.upTo ?? "")} onChange={e => setSlab(i, { upTo: num(e.target.value) })} />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Input type="number" min={0} max={100} step="0.01" className={`${NUM} w-20`} disabled={disabled} aria-label={`Slab ${i + 1} rate (%)`}
                          value={toPct(s.rate)} onChange={e => setSlab(i, { rate: fromPct(e.target.value || "0") })} />
                        <span className="text-[12px] text-surface-500">%</span>
                      </div>
                    </TableCell>
                    <TableCell className="w-8">
                      {!disabled && !last && rules.slabs.length > 2 && (
                        <Button size="icon" variant="ghost" aria-label={`Remove slab ${i + 1}`}
                          onClick={() => set({ slabs: rules.slabs.filter((_, j) => j !== i) })}><Trash2 className="size-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!disabled && <Button size="sm" variant="secondary" className="mt-2" onClick={addSlab}><Plus className="size-3.5" /> Add slab</Button>}
        </div>

        <div className="min-w-0">
          <div className="mb-2 text-[12px] font-semibold text-surface-700">Surcharge (on tax, with marginal relief)</div>
          <Table>
            <TableHeader><TableRow><TableHead>Taxable above ₹</TableHead><TableHead className="text-right">Rate</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rules.surcharge.map((s, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Input type="number" min={0} step="100000" className={NUM} disabled={disabled} aria-label={`Surcharge ${i + 1} threshold`}
                      value={String(s.above)} onChange={e => setSur(i, { above: num(e.target.value) })} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Input type="number" min={0} max={100} step="0.01" className={`${NUM} w-20`} disabled={disabled} aria-label={`Surcharge ${i + 1} rate (%)`}
                        value={toPct(s.rate)} onChange={e => setSur(i, { rate: fromPct(e.target.value || "0") })} />
                      <span className="text-[12px] text-surface-500">%</span>
                    </div>
                  </TableCell>
                  <TableCell className="w-8">
                    {!disabled && (
                      <Button size="icon" variant="ghost" aria-label={`Remove surcharge ${i + 1}`}
                        onClick={() => set({ surcharge: rules.surcharge.filter((_, j) => j !== i) })}><Trash2 className="size-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!disabled && (
            <Button size="sm" variant="secondary" className="mt-2"
              onClick={() => set({ surcharge: [...rules.surcharge, { above: (rules.surcharge.at(-1)?.above ?? 0) + 5000000, rate: 0 }] })}>
              <Plus className="size-3.5" /> Add tier
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────────────────────

export default function CompRulesPanel({ role }: { role: string }) {
  // `show` is stable; the toast context object itself changes with every toast.
  const { show: showToast } = useToast();
  const isSuperAdmin = role === Role.SUPER_HR_ADMIN;
  const [statutory, setStatutory] = useState<StatutoryConfig | null>(null);
  const [draft, setDraft] = useState<StatutoryConfig | null>(null);
  const [regimeTab, setRegimeTab] = useState<RegimeKey>("NEW_REGIME");
  // Every section opens read-only; the Super HR Admin clicks Edit to change it.
  const [editingTax, setEditingTax] = useState(false);
  // Every card opens and closes; the long tax card starts closed.
  const [taxOpen, setTaxOpen] = useState(false);

  const loadStatutory = useCallback(async () => {
    try {
      const r = await api.get<StatutoryConfig>("/compensation/statutory-config");
      setStatutory(r.data);
      setDraft(r.data);
    } catch (err) {
      logError("CompRulesPanel.loadStatutory", err);
      showToast(errMsg(err) ?? "Couldn't load the statutory settings", "error");
    }
  }, [showToast]);

  useEffect(() => { void loadStatutory(); }, [loadStatutory]);

  async function saveStatutory() {
    if (!draft) return;
    try {
      await api.put("/compensation/statutory-config", draft);
      showToast("Income tax settings saved", "success");
      setEditingTax(false);
      await loadStatutory();
    } catch (err) {
      logError("CompRulesPanel.saveStatutory", err);
      showToast(errMsg(err) ?? "Couldn't save the statutory settings", "error");
      throw err;
    }
  }

  const setD = (patch: Partial<StatutoryConfig>) => setDraft(d => (d ? { ...d, ...patch } : d));
  const statutoryDirty = JSON.stringify(draft) !== JSON.stringify(statutory);
  const taxEditable = isSuperAdmin && editingTax;

  return (
    <div className="flex flex-col gap-6">
      <p className="m-0 text-[12px] text-surface-500">
        Everything the CTC breakdown uses, in one place. Salary-structure changes go to Finance and apply once approved.
        Rates are shown as percentages (e.g. 12 = 12%).
      </p>

      {/* Salary structure (Standard earnings + Statutory PF/ESI/Gratuity) — versioned, Finance-verified */}
      <CompStructurePanel />

      {draft && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="cursor-pointer" onClick={() => setTaxOpen(o => !o)}>Income tax</CardTitle>
              <Badge tone="info">{statutory?.fiscalYear}</Badge>
              {!isSuperAdmin && <Badge tone="outline">Read only: Super HR Admin can edit</Badge>}
              <span className="ml-auto flex items-center gap-1">
                {isSuperAdmin && !editingTax && (
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => { setEditingTax(true); setTaxOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                )}
                <CollapseToggle open={taxOpen} onToggle={() => setTaxOpen(o => !o)} label="income tax" />
              </span>
            </div>
            <CardDescription>
              Check these against the current fiscal year's rules before running revisions. Income tax is worked out on gross pay
              minus fully exempt earnings, the standard deduction and, where enabled, employee PF (80C) and professional tax.
            </CardDescription>
          </CardHeader>
          {taxOpen && <CardContent className="flex flex-col gap-6">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
              <label htmlFor="stat-fy" className="flex min-w-0 flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-surface-500">Fiscal year</span>
                <Input id="stat-fy" className="h-8" disabled={!taxEditable} value={draft.fiscalYear}
                  onChange={e => setD({ fiscalYear: e.target.value })} />
              </label>
              <NumberField id="stat-cess" label="Health & education cess" suffix="%" disabled={!taxEditable}
                value={toPct(draft.cessRate)} onChange={v => setD({ cessRate: fromPct(v || "0") })} />
            </div>

            <Tabs value={regimeTab} onValueChange={v => setRegimeTab(v as RegimeKey)}>
              <TabsList>
                <TabsTrigger value="NEW_REGIME">New regime</TabsTrigger>
                <TabsTrigger value="OLD_REGIME">Old regime</TabsTrigger>
              </TabsList>
              {(["NEW_REGIME", "OLD_REGIME"] as const).map(k => (
                <TabsContent key={k} value={k} className="pt-4">
                  <RegimeEditor regime={k} rules={draft.taxRegimes[k]} disabled={!taxEditable}
                    onChange={r => setD({ taxRegimes: { ...draft.taxRegimes, [k]: r } })} />
                </TabsContent>
              ))}
            </Tabs>

            {taxEditable && (
              <div className="flex flex-wrap items-center justify-end gap-3">
                <span className="text-[12px] text-surface-500">
                  Standard deduction {rupees(draft.taxRegimes[regimeTab].standardDeduction)} · rebate up to {rupees(draft.taxRegimes[regimeTab].rebateIncomeLimit)}
                </span>
                <Button variant="secondary" onClick={() => { setDraft(statutory); setEditingTax(false); }}>Cancel</Button>
                <AsyncButton disabled={!statutoryDirty} onClick={() => saveStatutory()}>Save tax settings</AsyncButton>
              </div>
            )}
          </CardContent>}
        </Card>
      )}
    </div>
  );
}
